<template>
    <div class="relative">
        <div class="ml-3">
            {{ displayValue }}
        </div>
        <div class="absolute top-1/2 transform -translate-y-1/2 right-10">
            <dx-button
                v-if="showClearButton"
                hint="Сбросить значение"
                name="clearButton"
                icon='<svg width="64" height="64" viewBox="0 0 64 64" stroke="black">
<path d="M22.6066 21.3934C22.2161 21.0029 21.5829 21.0029 21.1924 21.3934C20.8019 21.7839 20.8019 22.4171 21.1924 22.8076L22.6066 21.3934ZM40.9914 42.6066C41.3819 42.9971 42.0151 42.9971 42.4056 42.6066C42.7961 42.2161 42.7961 41.5829 42.4056 41.1924L40.9914 42.6066ZM21.1924 41.1924C20.8019 41.5829 20.8019 42.2161 21.1924 42.6066C21.5829 42.9971 22.2161 42.9971 22.6066 42.6066L21.1924 41.1924ZM42.4056 22.8076C42.7961 22.4171 42.7961 21.7839 42.4056 21.3934C42.0151 21.0029 41.3819 21.0029 40.9914 21.3934L42.4056 22.8076ZM21.1924 22.8076L40.9914 42.6066L42.4056 41.1924L22.6066 21.3934L21.1924 22.8076ZM22.6066 42.6066L42.4056 22.8076L40.9914 21.3934L21.1924 41.1924L22.6066 42.6066Z" fill="black"/>
</svg>'
                @click="clearField"
            />
        </div>
        <div class="absolute top-1/2 transform -translate-y-1/2 right-1">
            <dx-button hint="Редактировать" name="editButton" icon="more" @click="togglePopupVisible" />
        </div>
    </div>

    <dx-popup
        :visible="isPopupVisible"
        :show-close-button="true"
        :animation="{ value: false }"
        :title="props.cellInfo.column.componentMeta.title"
        :width="props.cellInfo.column.componentMeta.width || '80vw'"
        @content-ready="onPopupContentReady"
        @shown="onPopupShown"
    >
        <div class="h-full w-full">
            <component
                v-if="isPopupVisible"
                :is="getComponent()"
                ref="componentRef"
                :currentValue="cellInfo.displayValue"
                :show-toolbar="false"
                :show-filter-panel="false"
                :row-click-call-back="rowClick"
                :cellInfo="cellInfo"
                :args="_cellInfo.column.componentMeta.args"
                :meta="cellInfo.column.componentMeta.meta || {}"
            />
        </div>
        <dx-toolbar-item v-if="saveButtonVisible" location="after" toolbar="bottom">
            <dx-button text="Сохранить" @click="saveHandler" />
        </dx-toolbar-item>
    </dx-popup>
</template>
<script>
/*
Чтобы кнопка Сбросить значение(Х) появилась в нужном столбце необходимо добавить в мету этого столбца настройку 'showClearButton: true' в editor.
Например, так - editor: { type: 'ai-popup', text: 'ename', showClearButton: true },
*/
export default {
    name: 'ai-popup',
};
</script>
<script setup>
import { DxButton, DxPopup } from 'devextreme-vue';
import { DxToolbarItem } from 'devextreme-vue/popup';
import { ref, computed, onMounted } from 'vue';
import { defineAsyncComponent } from 'vue';

const props = defineProps({
    displayValue: {
        type: String,
        default: '',
    },
    cellInfo: {
        type: Object,
        default: () => {},
    },
    onValueChanged: {
        type: Function,
        default: () => {},
    },
    onDotsClick: {
        type: Function,
        default: () => {},
    },
});

onMounted(() => {
    _cellInfo.value = props.cellInfo;
});
let _cellInfo = ref();
const isPopupVisible = ref(false);

const updatedRowRef = ref();
const keyExprRef = ref();
const rowRef = ref();
const componentRef = ref();

const emit = defineEmits(['select', 'endEditing', 'onFieldClearing']);

const onPopupContentReady = event => {
    const contentEl = event.component.content().parentElement;
    contentEl.className += '  dx-dropdowneditor-overlay';
};

const getComponent = () => {
    const componentName = props.cellInfo.column.componentMeta.name;
    if (componentName) return componentName;

    const componentPath = props.cellInfo.column.componentMeta.path;
    return defineAsyncComponent(() => import(`@/${componentPath}`));
};

const showClearButton = computed(() => {
    return props.cellInfo.value && props.cellInfo.column.editorMeta?.showClearButton;
});

const clearField = () => {
    props.onValueChanged(null);
    emit('onFieldClearing', props.cellInfo);
};

const togglePopupVisible = () => {
    const dbArgs = props.onDotsClick({ displayValue: props.displayValue, cellInfo: props.cellInfo });
    const args = { ..._cellInfo.value.column.componentMeta.args, ...dbArgs };
    _cellInfo.value.column.componentMeta.args = args;
    isPopupVisible.value = !isPopupVisible.value;
};

const onPopupShown = async () => {
    if (
        props.cellInfo.column.componentMeta.path ===
            'components/dictionary-components/ai-dictionary-virtual-grid.vue' &&
        !props.cellInfo.column.componentMeta.meta.autoLoadData
    ) {
        await componentRef.value.gridInstance.loadData();
    }
};

const rowClick = ({ keyExpr, row }) => {
    const { dataField, editorMeta, componentMeta } = props.cellInfo.column;
    const updatedRow = {};

    updatedRow[dataField] = row[keyExpr];
    updatedRow[editorMeta?.text] = row[componentMeta?.text];
    rowRef.value = row;
    updatedRowRef.value = updatedRow;
    keyExprRef.value = dataField;
    if (!saveButtonVisible.value) {
        emit('select', { row: updatedRow, keyExpr: dataField });
        props.onValueChanged(row[keyExpr]);
        togglePopupVisible();
        emit('endEditing', props.cellInfo);
    }
};
const saveButtonVisible = computed(() =>
    props.cellInfo.column.componentMeta.saveButtonVisible !== undefined
        ? props.cellInfo.column.componentMeta.saveButtonVisible
        : true
);
const saveHandler = () => {
    emit('select', { row: updatedRowRef.value, keyExpr: keyExprRef.value });

    props.onValueChanged(rowRef.value[keyExprRef.value]);

    togglePopupVisible();
    emit('endEditing', props.cellInfo);
};
</script>
